import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Power,
  Truck,
  Building2,
  Phone,
  User,
  Clock,
  Package,
} from "lucide-react";
import { showError, showInfo } from "@/store/errorStore";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";
const API = `${BACKEND_URL}`;

const CompaniesManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/companies`);
      const payload = response.data?.data ?? response.data ?? [];
      setCompanies(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
      showError("Erro ao carregar empresas");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAvailability = async (code, currentStatus) => {
    try {
      await axios.put(`${API}/companies/${code}/availability`, {
        available: !currentStatus,
      });
      showInfo(
        `Loja ${!currentStatus ? "Aberta" : "Fechada"} com sucesso!`,
        "Sucesso"
      );
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao alterar disponibilidade:", error);
      showError("Erro ao alterar disponibilidade da empresa");
    }
  };

  const handleToggleDelivery = async (code, currentStatus) => {
    try {
      await axios.put(`${API}/companies/${code}/delivery`, {
        delivery: !currentStatus,
      });
      showInfo(
        `Delivery ${!currentStatus ? "ativado" : "desativado"} com sucesso!`,
        "Sucesso"
      );
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao alterar delivery:", error);
      showError("Erro ao alterar status do delivery");
    }
  };

  const filteredCompanies = companies.filter((company) =>
    company.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Modal de Loading */}
      <Dialog open={loading} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando empresas...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-3xl font-bold text-gray-900">Gestão de Empresas</h2>
        <div className="w-full sm:w-auto">
          <Input
            placeholder="Buscar empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map((company) => (
          <Card
            key={company.id}
            className={`overflow-hidden hover:shadow-lg transition-shadow ${
              !company.available ? "opacity-60" : ""
            }`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">{company.name}</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      variant={company.available ? "default" : "secondary"}
                      className={
                        company.available
                          ? "bg-green-500 hover:bg-green-600"
                          : "bg-gray-500"
                      }
                    >
                      {company.available ? "Ativa" : "Inativa"}
                    </Badge>
                    <Badge
                      variant={company.delivery ? "default" : "secondary"}
                      className={
                        company.delivery
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-gray-400"
                      }
                    >
                      {company.delivery ? "Delivery ON" : "Delivery OFF"}
                    </Badge>
                  </div>
                </div>
                {company.logoUrl && (
                  <img
                    src={company.logoUrl}
                    alt={company.name}
                    className="w-16 h-16 object-contain rounded-lg ml-2"
                  />
                )}
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Descrição */}
              {company.description && (
                <p className="text-sm text-gray-600 line-clamp-2">
                  {company.description}
                </p>
              )}

              {/* Informações de Contato */}
              <div className="space-y-2 text-sm">
                {company.contactName && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="h-4 w-4 text-gray-500" />
                    <span>{company.contactName}</span>
                  </div>
                )}
                {company.contactPhone && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span>{company.contactPhone}</span>
                  </div>
                )}
              </div>

              {/* Horários de Funcionamento */}
              {(company.businessHoursWeekdays ||
                company.businessHoursWeekend) && (
                <div className="space-y-1 text-xs text-gray-600 bg-gray-50 p-3 rounded-md">
                  <div className="flex items-start gap-2">
                    <Clock className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      {company.businessHoursWeekdays && (
                        <p>{company.businessHoursWeekdays}</p>
                      )}
                      {company.businessHoursWeekend && (
                        <p>{company.businessHoursWeekend}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tipos de Serviço */}
              {(company.deliveryDescription ||
                company.pickupDescription ||
                company.eventsDescription) && (
                <div className="flex flex-wrap gap-2">
                  {company.deliveryDescription && (
                    <Badge variant="outline" className="text-xs">
                      {company.deliveryDescription}
                    </Badge>
                  )}
                  {company.pickupDescription && (
                    <Badge variant="outline" className="text-xs">
                      {company.pickupDescription}
                    </Badge>
                  )}
                  {company.eventsDescription && (
                    <Badge variant="outline" className="text-xs">
                      {company.eventsDescription}
                    </Badge>
                  )}
                </div>
              )}

              {/* Botões de Ação */}
              <div className="flex flex-col gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleToggleAvailability(company.code, company.available)
                  }
                  className={`w-full ${
                    company.available
                      ? "text-red-600 hover:text-red-700 hover:bg-red-50"
                      : "text-green-600 hover:text-green-700 hover:bg-green-50"
                  }`}
                >
                  <Power className="h-4 w-4 mr-2" />
                  {company.available ? "Fechar Loja" : "Abrir Loja"}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleToggleDelivery(company.code, company.delivery)
                  }
                  className={`w-full ${
                    company.delivery
                      ? "text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  }`}
                  disabled={!company.available}
                >
                  <Truck className="h-4 w-4 mr-2" />
                  {company.delivery ? "Desativar Delivery" : "Ativar Delivery"}
                </Button>
              </div>

              {/* Código da Empresa (para debug/referência) */}
              <div className="text-xs text-gray-400 text-center pt-2 border-t">
                Code: {company.code}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">
            {searchTerm
              ? "Nenhuma empresa encontrada com esse nome"
              : "Nenhuma empresa cadastrada"}
          </p>
        </div>
      )}
    </div>
  );
};

export default CompaniesManagement;
